import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Listing {
  id: string;
  title: string;
  suburb: string;
  state: string;
  size_sqm: number;
  price_per_month: number;
  status: 'active' | 'inactive' | 'rented' | 'paused' | 'deleted';
  images: string[];
  first_month_discount: boolean;
  space_type: string;
  created_at: string;
}

export default function MyListingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'inactive' | 'rented' | 'paused'>('all');
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBankBanner, setShowBankBanner] = useState(false);

  useEffect(() => {
    const fetchListings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('listings')
          .select('*')
          .eq('host_id', user.id)
          .neq('status', 'deleted')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setListings(data || []);

        // Check if host has connected bank account
        const { data: profileData } = await supabase
          .from('profiles')
          .select('stripe_account_id')
          .eq('id', user.id)
          .single();

        if (!profileData?.stripe_account_id) {
          setShowBankBanner(true);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [user]);

  const filteredListings = activeTab === 'all'
    ? listings
    : listings.filter(listing => listing.status === activeTab);

  const handlePauseListing = async (listingId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Are you sure you want to pause this listing? It will not be visible to renters.')) return;
    try {
      const { error } = await supabase.from('listings').update({ status: 'paused' }).eq('id', listingId).eq('host_id', user!.id);
      if (error) throw error;
      setListings(prev => prev.map(listing => listing.id === listingId ? { ...listing, status: 'paused' as const } : listing));
    } catch (err: any) {
      alert(`Failed to pause listing: ${err.message}`);
    }
  };

  const handleUnpauseListing = async (listingId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { error } = await supabase.from('listings').update({ status: 'active' }).eq('id', listingId).eq('host_id', user!.id);
      if (error) throw error;
      setListings(prev => prev.map(listing => listing.id === listingId ? { ...listing, status: 'active' as const } : listing));
    } catch (err: any) {
      alert(`Failed to unpause listing: ${err.message}`);
    }
  };

  const handleDeleteListing = async (listingId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this listing? This action cannot be undone.')) return;
    try {
      const { error } = await supabase.from('listings').update({ status: 'deleted' }).eq('id', listingId).eq('host_id', user!.id);
      if (error) throw error;
      setListings(prev => prev.filter(listing => listing.id !== listingId));
    } catch (err: any) {
      alert(`Failed to delete listing: ${err.message}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      rented: 'bg-blue-100 text-blue-800',
      paused: 'bg-amber-100 text-amber-800',
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      active: 'ri-checkbox-circle-line',
      inactive: 'ri-pause-circle-line',
      rented: 'ri-home-heart-line',
      paused: 'ri-pause-circle-line',
    };
    return icons[status as keyof typeof icons] || 'ri-pause-circle-line';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Page Header */}
          <div className="mb-8">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back</span>
            </button>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Listings</h1>
                <p className="text-gray-600 mt-2">Manage your storage spaces</p>
              </div>
              <button onClick={() => navigate('/list-space')} className="flex items-center justify-center gap-2 px-6 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-add-line text-xl"></i>
                <span>Add New Listing</span>
              </button>
            </div>
          </div>

          {/* Bank Account Warning Banner */}
          {showBankBanner && (
            <div className="mb-6 bg-amber-50 border-2 border-amber-400 rounded-xl p-4 flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-bank-line text-amber-600 text-xl"></i>
              </div>
              <div className="flex-1">
                <p className="text-amber-900 font-bold mb-1">Connect your bank account to receive payouts</p>
                <p className="text-amber-800 text-sm mb-3">You have active listings but no bank account connected. Without this, Stashly cannot transfer rental payments to you.</p>
                <button
                  onClick={() => navigate('/earnings')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-bank-line"></i>
                  Connect Bank Account
                </button>
              </div>
              <button onClick={() => setShowBankBanner(false)} className="text-amber-600 hover:text-amber-800 cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Listings</p>
                  <p className="text-3xl font-bold text-gray-900">{listings.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-home-4-line text-blue-600 text-2xl"></i>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active</p>
                  <p className="text-3xl font-bold text-green-600">{listings.filter(l => l.status === 'active').length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-green-600 text-2xl"></i>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Paused</p>
                  <p className="text-3xl font-bold text-amber-600">{listings.filter(l => l.status === 'paused').length}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-pause-circle-line text-amber-600 text-2xl"></i>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Rented</p>
                  <p className="text-3xl font-bold text-blue-600">{listings.filter(l => l.status === 'rented').length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-home-heart-line text-blue-600 text-2xl"></i>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Inactive</p>
                  <p className="text-3xl font-bold text-gray-600">{listings.filter(l => l.status === 'inactive').length}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-pause-circle-line text-gray-600 text-2xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="relative">
              <div className="flex gap-2 p-2 overflow-x-auto scrollbar-hide">
                {(['all', 'active', 'paused', 'inactive', 'rented'] as const).map((tab) => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab ? 'bg-[#14B8A6] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    <span className="ml-2 text-sm">({tab === 'all' ? listings.length : listings.filter(l => l.status === tab).length})</span>
                  </button>
                ))}
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none md:hidden"></div>
            </div>
          </div>

          {/* Listings Grid */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-12 h-12 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your listings...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-4xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Listings</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-6 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line"></i>
                <span>Retry</span>
              </button>
            </div>
          ) : filteredListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredListings.map((listing) => (
                <div key={listing.id} className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow ${listing.status === 'paused' ? 'opacity-75' : ''}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={listing.images?.[0] || 'https://readdy.ai/api/search-image?query=empty%20storage%20space%20placeholder%20with%20clean%20white%20walls%20and%20concrete%20floor%20minimalist%20interior%20design%20professional%20real%20estate%20photography&width=400&height=300&seq=placeholder&orientation=landscape'}
                      alt={listing.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusBadge(listing.status)}`}>
                        <i className={getStatusIcon(listing.status)}></i>
                        {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
                      </span>
                    </div>
                    {listing.first_month_discount && (
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          <i className="ri-price-tag-3-line mr-1"></i>First Month Discount
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{listing.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <i className="ri-map-pin-line"></i>
                      <span>{listing.suburb}, {listing.state}</span>
                    </div>
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-ruler-line"></i>
                        <span>{listing.size_sqm}m²</span>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#14B8A6]">${listing.price_per_month}</p>
                        <p className="text-xs text-gray-500">per month</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/list-space?id=${listing.id}`)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm">
                          <i className="ri-edit-line mr-1"></i>Edit
                        </button>
                        <button onClick={() => navigate(`/listing/${listing.id}`)} className="flex-1 px-4 py-2 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap text-sm">
                          <i className="ri-eye-line mr-1"></i>View
                        </button>
                      </div>
                      <div className="flex gap-2">
                        {listing.status === 'active' ? (
                          <button onClick={(e) => handlePauseListing(listing.id, e)} className="flex-1 px-4 py-2 bg-amber-100 text-amber-700 font-semibold rounded-lg hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap text-sm">
                            <i className="ri-pause-circle-line mr-1"></i>Pause
                          </button>
                        ) : listing.status === 'paused' ? (
                          <button onClick={(e) => handleUnpauseListing(listing.id, e)} className="flex-1 px-4 py-2 bg-green-100 text-green-700 font-semibold rounded-lg hover:bg-green-200 transition-colors cursor-pointer whitespace-nowrap text-sm">
                            <i className="ri-play-circle-line mr-1"></i>Unpause
                          </button>
                        ) : null}
                        <button onClick={(e) => handleDeleteListing(listing.id, e)} className="flex-1 px-4 py-2 bg-red-100 text-red-700 font-semibold rounded-lg hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap text-sm">
                          <i className="ri-delete-bin-line mr-1"></i>Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-4-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No listings found</h3>
              <p className="text-gray-600 mb-6">
                {activeTab === 'all' ? "You haven't created any listings yet" : `You don't have any ${activeTab} listings`}
              </p>
              <button onClick={() => navigate('/list-space')} className="inline-flex items-center gap-2 px-6 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-add-line"></i>
                <span>Create Your First Listing</span>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
